import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { GoogleMap } from '@angular/google-maps';
import { LocationInterface, SuggestedLocationDataChange } from '../../../models/location.interface';
import { LocationValidationStatus } from '../../../enums/location-validation-status.enum';
import { LocationService } from '../../../services/location.service';
import { ToastrService } from 'ngx-toastr';

declare const google: any;

@Component({
    selector: 'address-validation-detail',
    templateUrl: './address-validation-detail.component.html',
    styleUrls: ['./address-validation-detail.component.scss'],
    standalone: false
})
export class AddressValidationDetailComponent implements OnInit, OnChanges, OnDestroy {
  @Input() location!: LocationInterface;
  @Input() suggestedData?: SuggestedLocationDataChange;
  
  @ViewChild(GoogleMap) googleMapComponent?: GoogleMap;

  validationStatuses = LocationValidationStatus;
  
  mapOptions: google.maps.MapOptions = {
    mapTypeId: 'roadmap',
    zoomControl: true,
    scrollwheel: true,
    disableDoubleClickZoom: false,
    maxZoom: 20,
    minZoom: 4,
  };

  mapCenter: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  mapZoom = 15;
  isMapLoaded = false;
  currentMarkerPosition?: google.maps.LatLngLiteral;
  suggestedMarkerPosition?: google.maps.LatLngLiteral;
  
  similarAddresses: LocationInterface[] = [];
  loadingSimilarAddresses = false;

  currentMarkerOptions: google.maps.MarkerOptions = {
    draggable: false,
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: '#4285F4',
      fillOpacity: 1,
      strokeColor: 'white',
      strokeWeight: 2,
    },
    label: {
      text: 'C',
      color: 'white',
      fontWeight: 'bold'
    }
  };

  suggestedMarkerOptions: google.maps.MarkerOptions = {
    draggable: false,
    icon: {
      path: google.maps.SymbolPath.CIRCLE,
      scale: 10,
      fillColor: '#34A853',
      fillOpacity: 1,
      strokeColor: 'white',
      strokeWeight: 2,
    },
    label: {
      text: 'S',
      color: 'white',
      fontWeight: 'bold'
    }
  };

  polylineOptions: google.maps.PolylineOptions = {
    strokeColor: '#FF6B6B',
    strokeOpacity: 0.7,
    strokeWeight: 2,
    geodesic: true
  };

  polylinePath: google.maps.LatLngLiteral[] = [];
  
  private hasGeocodedCurrentCountry = false;
  private hasGeocodedSuggestedCountry = false;
  private isMapInitialized = false;
  
  private static countryCache = new Map<string, { lat: number, lng: number }>();
  private static readonly DEFAULT_COUNTRY_CENTERS: { [key: string]: { lat: number, lng: number } } = {
    'DE': { lat: 51.1657, lng: 10.4515 },
    'PL': { lat: 51.9194, lng: 19.1451 },
    'FR': { lat: 46.2276, lng: 2.2137 },
    'IT': { lat: 41.8719, lng: 12.5674 },
    'ES': { lat: 40.4637, lng: -3.7492 },
    'NL': { lat: 52.1326, lng: 5.2913 },
    'BE': { lat: 50.5039, lng: 4.4699 },
    'AT': { lat: 47.5162, lng: 14.5501 },
    'DK': { lat: 56.2639, lng: 9.5018 },
    'SE': { lat: 60.1282, lng: 18.6435 },
    'NO': { lat: 60.4720, lng: 8.4689 },
    'FI': { lat: 61.9241, lng: 25.7482 },
    'GB': { lat: 55.3781, lng: -3.4360 },
    'IE': { lat: 53.4129, lng: -8.2439 },
    'CH': { lat: 46.8182, lng: 8.2275 },
    'CZ': { lat: 49.8175, lng: 15.4730 },
    'PT': { lat: 39.3999, lng: -8.2245 },
    'GR': { lat: 39.0742, lng: 21.8243 },
    'HU': { lat: 47.1625, lng: 19.5033 },
    'RO': { lat: 45.9432, lng: 24.9668 },
  };

  constructor(
    private locationService: LocationService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.initializeMap();
    this.loadSimilarAddresses();
  }

  loadMap(): void {
    this.isMapLoaded = true;
  }

  loadSimilarAddresses(): void {
    if (!this.location.address?.street) {
      return;
    }

    this.loadingSimilarAddresses = true;
    this.locationService.getSimilarValidatedAddresses(
      this.location.address.street
    ).subscribe({
      next: (response) => {
        this.similarAddresses = response.content.filter(
          (addr: LocationInterface) => addr.extras.geodata_id !== this.location.extras.geodata_id
        );
        this.loadingSimilarAddresses = false;
      },
      error: () => {
        this.loadingSimilarAddresses = false;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestedData'] && !changes['suggestedData'].firstChange) {
      this.updateMapWithSuggestedData();
    }
    
    if (changes['location'] && !changes['location'].firstChange) {
      this.resetState();
      this.initializeMap();
    }
  }

  ngOnDestroy(): void {}

  isDifferent(current: any, suggested: any): boolean {
    return current !== suggested;
  }

  validateAddress(): void {
    if (!this.hasValidCoordinates()) {
      this.toastr.warning('Location has no coordinates', 'Warning');
      return;
    }
    
    this.locationService.locationValidate(this.location.extras.geodata_id, null).subscribe(
      () => this.toastr.success('Location has been validated', 'Success'),
      () => this.toastr.error('Location validation failed', 'Error')
    );
  }

  approveSuggestion(): void {
    this.locationService.locationApproveSuggestion(this.location.extras.geodata_id, null).subscribe(
      () => this.toastr.success('Suggestion approved', 'Success'),
      () => this.toastr.error('Failed to approve suggestion', 'Error')
    );
  }

  rejectSuggestion(): void {
    this.locationService.locationRejectSuggestion(this.location.extras.geodata_id, null).subscribe(
      () => this.toastr.success('Suggestion rejected', 'Success'),
      () => this.toastr.error('Failed to reject suggestion', 'Error')
    );
  }

  private initializeMap(): void {
    if (this.isMapInitialized) {
      return;
    }
    
    const { lat, lng } = this.getCurrentCoordinates();

    if (!this.isValidCoordinate(lat) || !this.isValidCoordinate(lng)) {
      return;
    }

    this.isMapInitialized = true;
    
    if (this.isAtEquator(lat, lng)) {
      this.handleEquatorCoordinates();
      return;
    }

    this.setCurrentMarker(lat, lng);
    this.handleSuggestedData(lat, lng);
  }

  private updateMapWithSuggestedData(): void {
    if (!this.suggestedData?.lat_lon) {
      return;
    }

    const { lat: currentLat, lng: currentLng } = this.getCurrentCoordinates();
    const { lat: suggestedLat, lng: suggestedLng } = this.suggestedData.lat_lon;

    if (!this.isValidCoordinate(currentLat) || !this.isValidCoordinate(currentLng) ||
        !this.isValidCoordinate(suggestedLat) || !this.isValidCoordinate(suggestedLng)) {
      return;
    }
    
    if (this.isAtEquator(suggestedLat, suggestedLng) && this.suggestedData.country_code) {
      this.geocodeSuggestedCountry(this.suggestedData.country_code, currentLat, currentLng);
      return;
    }

    this.setSuggestedMarker(suggestedLat, suggestedLng);
    this.drawPolyline(currentLat, currentLng, suggestedLat, suggestedLng);
    this.fitMapBounds(currentLat, currentLng, suggestedLat, suggestedLng);
  }

  private geocodeCountry(countryCode: string): void {
    if (this.hasGeocodedCurrentCountry) {
      return;
    }
    this.hasGeocodedCurrentCountry = true;
    
    const cached = this.getCountryFromCache(countryCode);
    if (cached) {
      this.setCurrentMarker(cached.lat, cached.lng);
      this.mapZoom = 6;
      return;
    }
    
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ 
      address: countryCode,
      componentRestrictions: { country: countryCode }
    }, (results: any, status: any) => {
      if (status === 'OK' && results?.length > 0) {
        const center = results[0].geometry.location;
        const lat = center.lat();
        const lng = center.lng();
        
        this.cacheCountryCenter(countryCode, lat, lng);
        this.setCurrentMarker(lat, lng);
        this.mapZoom = 6;
      } else {
        this.setCurrentMarker(0, 0);
        this.mapZoom = 2;
      }
    });
  }

  private geocodeSuggestedCountry(countryCode: string, currentLat: number, currentLng: number): void {
    if (this.hasGeocodedSuggestedCountry) {
      return;
    }
    this.hasGeocodedSuggestedCountry = true;
    
    const cached = this.getCountryFromCache(countryCode);
    if (cached) {
      this.setSuggestedMarker(cached.lat, cached.lng);
      this.drawPolyline(currentLat, currentLng, cached.lat, cached.lng);
      this.fitMapBounds(currentLat, currentLng, cached.lat, cached.lng);
      return;
    }
    
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ 
      address: countryCode,
      componentRestrictions: { country: countryCode }
    }, (results: any, status: any) => {
      if (status === 'OK' && results?.length > 0) {
        const center = results[0].geometry.location;
        const centerLat = center.lat();
        const centerLng = center.lng();
        
        this.cacheCountryCenter(countryCode, centerLat, centerLng);
        this.setSuggestedMarker(centerLat, centerLng);
        this.drawPolyline(currentLat, currentLng, centerLat, centerLng);
        this.fitMapBounds(currentLat, currentLng, centerLat, centerLng);
      }
    });
  }

  private handleEquatorCoordinates(): void {
    const countryCode = this.location.address?.country?.code;
    if (countryCode) {
      this.geocodeCountry(countryCode);
    }
  }

  private handleSuggestedData(currentLat: number, currentLng: number): void {
    if (!this.suggestedData?.lat_lon) {
      return;
    }

    const suggestedLat = this.suggestedData.lat_lon.lat;
    const suggestedLng = this.suggestedData.lat_lon.lng;

    if (!suggestedLat || !suggestedLng || 
        (suggestedLat === currentLat && suggestedLng === currentLng)) {
      return;
    }

    if (this.isAtEquator(suggestedLat, suggestedLng) && this.suggestedData.country_code) {
      this.geocodeSuggestedCountry(this.suggestedData.country_code, currentLat, currentLng);
    } else {
      this.setSuggestedMarker(suggestedLat, suggestedLng);
      this.drawPolyline(currentLat, currentLng, suggestedLat, suggestedLng);
      this.adjustMapBounds();
    }
  }

  private adjustMapBounds(): void {
    setTimeout(() => {
      if (this.googleMapComponent?.googleMap && 
          this.currentMarkerPosition && 
          this.suggestedMarkerPosition) {
        this.fitMapBounds(
          this.currentMarkerPosition.lat,
          this.currentMarkerPosition.lng,
          this.suggestedMarkerPosition.lat,
          this.suggestedMarkerPosition.lng
        );
      }
    }, 100);
  }

  private setCurrentMarker(lat: number, lng: number): void {
    this.currentMarkerPosition = { lat, lng };
    this.mapCenter = { lat, lng };
  }

  private setSuggestedMarker(lat: number, lng: number): void {
    this.suggestedMarkerPosition = { lat, lng };
  }

  private drawPolyline(lat1: number, lng1: number, lat2: number, lng2: number): void {
    this.polylinePath = [
      { lat: lat1, lng: lng1 },
      { lat: lat2, lng: lng2 }
    ];
  }

  private fitMapBounds(lat1: number, lng1: number, lat2: number, lng2: number): void {
    if (this.googleMapComponent?.googleMap) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat: lat1, lng: lng1 });
      bounds.extend({ lat: lat2, lng: lng2 });
      this.googleMapComponent.googleMap.fitBounds(bounds);
    }
  }

  private getCurrentCoordinates(): { lat: number, lng: number } {
    return {
      lat: this.location.address?.geo_decimal?.lat ?? 0,
      lng: this.location.address?.geo_decimal?.lng ?? 0
    };
  }

  private isValidCoordinate(value: number | null | undefined): boolean {
    return value !== null && value !== undefined;
  }

  private isAtEquator(lat: number, lng: number): boolean {
    return lat === 0 && lng === 0;
  }

  private hasValidCoordinates(): boolean {
    return !!this.location.address.geo_decimal.lng && 
           !!this.location.address.geo_decimal.lat;
  }

  private resetState(): void {
    this.hasGeocodedCurrentCountry = false;
    this.hasGeocodedSuggestedCountry = false;
    this.isMapInitialized = false;
  }

  private getCountryFromCache(countryCode: string): { lat: number, lng: number } | null {
    if (AddressValidationDetailComponent.DEFAULT_COUNTRY_CENTERS[countryCode]) {
      return AddressValidationDetailComponent.DEFAULT_COUNTRY_CENTERS[countryCode];
    }
    
    return AddressValidationDetailComponent.countryCache.get(countryCode) || null;
  }

  private cacheCountryCenter(countryCode: string, lat: number, lng: number): void {
    AddressValidationDetailComponent.countryCache.set(countryCode, { lat, lng });
  }
}
